/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Button,
  Collapse,
  Grid,
  IconButton,
  Stack
} from "@mui/material";
import { ReactComponent as ArrowDown } from "assets/images/ArrowDown.svg";
import { ReactComponent as ClosedIcon } from "assets/images/CloseIcon.svg";
import ErrorIcon from "assets/images/warning-2.svg";
import { CircularLoaderFullPage, SearchField } from "components/core";
import { Formik, useFormik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  callToCloudFunction,
  getDataFromFirestore
} from "services/api/apiRequests";
import { CF_URLS } from "services/api/endpoints";
import { getSecurityGroups } from "services/api/query";
import spacing from "styles/spacing";
import { ERROR_TYPES, securityGroupsFilterId } from "utils/constants";
import {
  dataDogErrorLog,
  findErrorMessage,
  getAuthenticatedUserBranch,
  getSortedData,
  handleRefId
} from "utils/helpers";
import { GroupManagementStyles } from "./GroupManagementStyles";
import GroupManagementTable from "./GroupManagementTable";
import { securityGroupManagementSchema } from "./Schema";

const GroupManagementLayout = () => {
  //form initial state
  const initialGroupManagement = {
    securityGroup: ""
  };

  //form initialize using useFormik
  const securityGroupManagementForm = useFormik({
    initialValues: initialGroupManagement,
    validationSchema: securityGroupManagementSchema
  });

  const { t } = useTranslation();
  const districtId = getAuthenticatedUserBranch();

  const [securityGroups, setSecurityGroups] = useState([]);
  const [securityGroupDefault, setSecurityGroupDefault] = useState("");
  const [isResetGrid, setIsResetGrid] = useState(true);
  const [searchResults, setSearchResults] = useState([]);
  const [disableFind, setDisableFind] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingSecurityGroups, setLoadingSecurityGroups] = useState(false);
  const [throwError, setThrowError] = useState(false);
  const [throwErrorMessage, setThrowErrorMessage] = useState(null);

  //get security groups
  const fetchSecurityGroups = async () => {
    try {
      setLoadingSecurityGroups(true);
      const result = await getSecurityGroups(districtId);
      const filterByFilterFlag = result.filter(
        (secGroup) =>
          secGroup?.filter_flag === securityGroupsFilterId.one ||
          securityGroupsFilterId.zero
      );
      //sort data by security sort id
      const sortedSecurityGroups = filterByFilterFlag.sort(
        (a, b) => parseInt(a?.sort_id) - parseInt(b?.sort_id)
      );

      const response = sortedSecurityGroups.map((res) => ({
        value: res?.security_group_id,
        label: res?.code.trim()
      }));
      setSecurityGroups(response);
    } catch (e) {
      const { refId, message } = handleRefId(
        e.message || findErrorMessage(ERROR_TYPES.ISSUE)
      );
      dataDogErrorLog(message, "", { error: e }, refId);
      setThrowErrorMessage(message);
      setThrowError(true);
    } finally {
      setLoadingSecurityGroups(false);
    }
  };

  //handle clear
  const handleReset = () => {
    securityGroupManagementForm.resetForm();
    setSecurityGroupDefault("");
    setSearchResults([]);
    setLoading(false);
    setThrowError(false);
    setIsResetGrid(true);
    setDisableFind(false);
  };

  //handle security group change
  const handleSecurityGroupChange = (event, value) => {
    handleReset();
    setSecurityGroupDefault(value);
    securityGroupManagementForm.handleChange({
      target: { name: "securityGroup", value: value?.value }
    });
  };

  //fetch table data
  const fetchTableData = async () => {
    try {
      setLoading(true);
      const reqBody = JSON.stringify({
        main_district_id: districtId,
        group_id: securityGroupManagementForm.values?.securityGroup
      });
      const response = await callToCloudFunction(
        reqBody,
        CF_URLS.securityGroupManagement.getSecurityGroupDetails
      );
      const data = await getDataFromFirestore(response, 1, response.data.docId);
      const securityGroupData = data.data[0];
      if (securityGroupData) {
        let sortedData = getSortedData(securityGroupData, "descr", "asc");
        setSearchResults(sortedData);
        setDisableFind(true);
        setIsResetGrid(false);
      } else if (data?.error) {
        setThrowErrorMessage(data?.error);
        setThrowError(true);
      }
    } catch (e) {
      const { refId, message } = handleRefId(
        e.message || findErrorMessage(ERROR_TYPES.ISSUE)
      );
      dataDogErrorLog(
        message,
        CF_URLS.securityGroupManagement.getSecurityGroupDetails,
        { error: e },
        refId
      );
      setThrowErrorMessage(message);
      setThrowError(true);
    } finally {
      setLoading(false);
    }
  };

  //handle find
  const handleFind = async () => {
    await fetchTableData();
  };

  useEffect(() => {
    districtId && fetchSecurityGroups();
  }, [districtId]);

  return (
    <>
      {/*Circular loader*/}
      <CircularLoaderFullPage loading={loading} />
      {/*error alert box*/}
      {throwError && (
        <Collapse in={throwError}>
          <Alert
            severity="error"
            icon={<img src={ErrorIcon} alt="error" />}
            sx={GroupManagementStyles.alertBox}
            action={
              <IconButton
                aria-label={t("common.close")}
                color="inherit"
                size="small"
                onClick={() => {
                  setThrowError(false);
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {throwErrorMessage}
          </Alert>
        </Collapse>
      )}

      <Formik>
        <form>
          <Grid
            container
            display={"flex"}
            direction={"column"}
            flexDirection={"column"}
            gap={spacing.verticalMargin20}
          >
            <Grid container spacing={spacing.verticalMargin20}>
              {/*Security group search field*/}
              <Grid item xs={12} md={6} lg={3} xl={3}>
                <SearchField
                  label={t("securityGroupManagement.securityGroup")}
                  id="security-group-search"
                  options={securityGroups}
                  clearIcon={<ClosedIcon />}
                  popupIcon={<ArrowDown />}
                  value={securityGroupDefault}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  onChange={handleSecurityGroupChange}
                  defaultValue=""
                  error={!!securityGroupManagementForm.errors.securityGroup}
                  helperText={securityGroupManagementForm.errors.securityGroup}
                  loading={loadingSecurityGroups}
                  required
                />
              </Grid>
            </Grid>
            <Stack direction="row" spacing={spacing.verticalMargin20}>
              {/*Clear button*/}
              <Button
                id="security-group-clear"
                variant="outlined"
                onClick={handleReset}
              >
                {t("common.clear")}
              </Button>
              {/*Find button*/}
              <Button
                id="security-group-find"
                variant="contained"
                type="button"
                onClick={handleFind}
                disabled={
                  disableFind ||
                  Boolean(!securityGroupManagementForm.values?.securityGroup)
                }
              >
                {t("common.find")}
              </Button>
            </Stack>
            <GroupManagementTable
              isResetGrid={isResetGrid}
              searchResults={searchResults}
              securityGroupManagementForm={securityGroupManagementForm}
            />
          </Grid>
        </form>
      </Formik>
    </>
  );
};

export default GroupManagementLayout;
